import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import css from './style.module.css';
import { MarktingContext } from '../../Context/MarktingContext';
import CommentReplyInput from './CommentReplyInput';
import AgeDisplay from './AgeDisplay';
import { motion, AnimatePresence } from 'framer-motion';

const BOT_NAME = 'PIRM AI Bot';

const FILTERS = [
    { key: 'all', label: 'All', icon: 'fa-solid fa-layer-group' },
    { key: 'bot', label: 'PIRM AI Bot', icon: 'fa-solid fa-robot' },
    { key: 'user', label: 'People', icon: 'fa-solid fa-user' },
];

const STATUS_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'open', label: 'Open' },
    { key: 'resolved', label: 'Resolved' },
];

const isBotComment = (comment) => comment?.created_by === BOT_NAME;

function CommentAvatar({ name, size = 30 }) {
    const isBot = name === BOT_NAME;
    return (
        <div
            className={isBot ? css.botAvatar : css.userAvatar}
            style={{ width: size, height: size }}
            aria-label={isBot ? 'PIRM AI Bot avatar' : `${name} avatar`}
        >
            {isBot ? (
                <i className="fa-solid fa-robot" />
            ) : (
                <span className="fw-bold text-light">
                    {name?.charAt(0).toUpperCase()}
                </span>
            )}
        </div>
    );
}

function AuthorMeta({ comment }) {
    const bot = isBotComment(comment);
    return (
        <div className="d-flex align-items-center">
            <CommentAvatar name={comment.created_by} />
            <div className="ms-2">
                <div className="d-flex align-items-center gap-2">
                    <p className="m-0" style={{ fontSize: '1.125rem', fontWeight: 500 }}>
                        {comment.created_by}
                    </p>
                    {bot && (
                        <span className={css.botBadge}>
                            <i className="fa-solid fa-sparkles me-1" />AI
                        </span>
                    )}
                </div>
                <p className="text-muted m-0" style={{ fontSize: '0.85rem' }}>
                    <AgeDisplay createdAt={comment.createdAt} />
                </p>
            </div>
        </div>
    );
}

export default function DocumentComments({
    id,
    comments,
    setComments,
    activeComment,
    loginToken,
    archived,
    onEyeIconClick,
}) {
    const commentRefs = useRef({});
    const [activeCommentId, setActiveCommentId] = useState(null);
    const { marktingData, archivedData } = useContext(MarktingContext);
    const [expandedComments, setExpandedComments] = useState({});
    const [authorFilter, setAuthorFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (!marktingData.length && !archivedData.length) return;

        const element = marktingData.find(
            item =>
                item.documentId === id ||
                item.version?.some(v => v.versionId === id)
        );

        const archivedElement = archivedData.find(
            item =>
                item.documentId === id ||
                item.version?.some(v => v.versionId === id)
        );

        if (!element && !archivedElement) {
            setComments([]);
            return;
        }

        if (element?.documentId === id || archivedElement?.documentId === id) {
            setComments(element ? element.Comments : archivedElement ? archivedElement.Comments : []);
        } else {
            const version = element ? element.version.find(v => v.versionId === id) : archivedElement?.version.find(v => v.versionId === id);
            setComments(version?.Comments ?? []);
        }
    }, [marktingData, id, setComments, archivedData, archived]);

    const counts = useMemo(() => {
        const list = comments ?? [];
        return {
            all: list.length,
            bot: list.filter(isBotComment).length,
            user: list.filter((c) => !isBotComment(c)).length,
            open: list.filter((c) => !c.resolved).length,
            resolved: list.filter((c) => c.resolved).length,
        };
    }, [comments]);

    const visibleComments = useMemo(() => {
        const list = comments ?? [];
        const q = search.trim().toLowerCase();
        return list
            .filter((c) => {
                if (authorFilter === 'bot' && !isBotComment(c)) return false;
                if (authorFilter === 'user' && isBotComment(c)) return false;
                if (statusFilter === 'open' && c.resolved) return false;
                if (statusFilter === 'resolved' && !c.resolved) return false;
                if (!q) return true;
                const haystack = [
                    c.comment,
                    c.created_by,
                    ...(c.commentsReplay?.map((r) => `${r.commentReplay} ${r.created_by}`) ?? []),
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                return haystack.includes(q);
            })
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .sort((a, b) => a.resolved - b.resolved);
    }, [comments, authorFilter, statusFilter, search]);

    const toggle = (commentId) => {
        setExpandedComments((prev) => ({
            ...prev,
            [commentId]: !prev[commentId],
        }));
    };

    const scrollToComment = (key) => {
        const elem = commentRefs.current[key];
        if (elem) {
            const items = Array.isArray(elem) ? elem : [elem];
            items.forEach((el) => el.scrollIntoView({ behavior: 'smooth', block: 'center' }));
            setActiveCommentId(key);
        } else {
            setActiveCommentId(null);
        }
    };

    const handleEyeIconClick = (comment, e) => {
        e.stopPropagation();
        if (onEyeIconClick) onEyeIconClick(comment);
        setActiveCommentId(comment.id);
    };

    useEffect(() => {
        if (activeComment) {
            const key = activeComment.elementId || activeComment.id;
            scrollToComment(key);
        }
    }, [activeComment]);

    const renderReplies = (comment) =>
        comment.commentsReplay
            ?.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
            .map((r) => (
                <div key={r.id}>
                    <hr />
                    <div className="d-flex mb-2 align-items-center">
                        <CommentAvatar name={r.created_by} size={28} />
                        <div className="ms-2">
                            <p className="m-0" style={{ fontSize: '1rem', fontWeight: 500 }}>
                                {r.created_by}
                            </p>
                            <p className="text-muted m-0" style={{ fontSize: '0.8rem' }}>
                                <AgeDisplay createdAt={r.createdAt} />
                            </p>
                        </div>
                    </div>
                    <p className="ms-1 overflow-auto">{r.commentReplay}</p>
                </div>
            ));

    const Unresolved = (comment) => {
        const bot = isBotComment(comment);
        return (
            <motion.div
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                ref={(el) => {
                    commentRefs.current[comment.id] = el;
                    if (comment.elementId) commentRefs.current[comment.elementId] = el;
                }}
                key={comment.id}
                className={`${css.commentBox} ${bot ? css.botCommentBox : ''} ${activeCommentId === comment.id || activeCommentId === comment.elementId
                    ? css.activeCommentBox
                    : ''
                    }`}
            >
                <div className="d-flex mb-2 justify-content-between align-items-start">
                    <AuthorMeta comment={comment} />

                    <motion.div
                        className="d-flex justify-content-center align-items-center"
                        style={{
                            cursor: 'pointer',
                            padding: '5px',
                            borderRadius: '50%',
                            width: '35px',
                            height: '35px',
                        }}
                        whileHover={{
                            backgroundColor: 'rgba(195, 29, 138, 0.1)',
                            scale: 1.1,
                        }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => handleEyeIconClick(comment, e)}
                        title="View comment on image"
                    >
                        <i
                            className="fa-solid fa-eye"
                            style={{ color: 'var(--accent)', fontSize: '16px' }}
                        />
                    </motion.div>
                </div>

                <p className="mb-3 overflow-auto">{comment.comment}</p>

                {!archived && (
                    <CommentReplyInput
                        archived={archived}
                        loginToken={loginToken}
                        comentId={comment.id}
                        resolvedStatus={comment.resolved}
                        resolvedBy={comment.resolvedBy}
                        resolvedAt={comment.resolvedAt}
                    />
                )}
                {renderReplies(comment)}
            </motion.div>
        );
    };

    const Resolved = (comment) => {
        const isExpanded = !!expandedComments[comment.id];
        const bot = isBotComment(comment);
        return (
            <motion.div
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                ref={(el) => {
                    commentRefs.current[comment.id] = el;
                    if (comment.elementId) commentRefs.current[comment.elementId] = el;
                }}
                key={comment.id}
                className={`${css.commentBox} ${bot ? css.botCommentBox : ''} d-flex flex-column mb-2 py-2 ${activeCommentId === comment.id || activeCommentId === comment.elementId
                    ? css.activeCommentBox
                    : ''
                    }`}
            >
                <div className="d-flex justify-content-between align-items-center">
                    <AuthorMeta comment={comment} />

                    <div className="d-flex align-items-center gap-2">
                        <motion.div
                            className="d-flex justify-content-center align-items-center"
                            style={{
                                cursor: 'pointer',
                                padding: '5px',
                                borderRadius: '50%',
                                width: '35px',
                                height: '35px',
                            }}
                            whileHover={{
                                backgroundColor: 'rgba(195, 29, 138, 0.1)',
                                scale: 1.1,
                            }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => handleEyeIconClick(comment, e)}
                            title="View comment on image"
                        >
                            <i
                                className="fa-solid fa-eye"
                                style={{ color: 'var(--accent)', fontSize: '16px' }}
                            />
                        </motion.div>

                        <span className={css.commentSeeMore} onClick={() => toggle(comment.id)}>
                            {isExpanded ? <i className="fa-solid fa-minus" /> : <i className="fa-solid fa-plus" />}
                            <span className={css.tooltip}>{isExpanded ? 'See less' : 'See more'}</span>
                        </span>

                        <motion.div
                            className="d-flex justify-content-center align-items-center rounded-circle"
                            style={{
                                width: 30,
                                height: 30,
                                border: '1px solid #B3B3B3',
                                backgroundColor: 'var(--surface)',
                            }}
                            whileTap={{ scale: 0.9 }}
                            whileHover={{ backgroundColor: 'rgba(77, 30, 77, 0.2)' }}
                            title="Resolved"
                        >
                            <motion.span
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: 'spring', stiffness: 300 }}
                                className="text-success"
                                style={{ fontSize: '0.8rem' }}
                            >
                                ✔
                            </motion.span>
                        </motion.div>
                    </div>
                </div>

                <AnimatePresence initial={false}>
                    {isExpanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.6, ease: 'easeInOut' }}
                            style={{ overflow: 'hidden' }}
                        >
                            <p className="overflow-auto">{comment.comment}</p>
                            <CommentReplyInput
                                archived={archived}
                                loginToken={loginToken}
                                comentId={comment.id}
                                resolvedStatus={comment.resolved}
                                resolvedBy={comment.resolvedBy}
                                resolvedAt={comment.resolvedAt}
                            />
                            {renderReplies(comment)}
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        );
    };

    const totalLoaded = comments?.length ?? 0;

    return (
        <div className="vh-100 container-fluid px-2 py-4 overflow-y-auto overflow-x-hidden">
            <div className="row">
                <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <h3 className={css.headerText}>Comments feed</h3>
                    <span className={css.feedTotal}>{totalLoaded} total</span>
                </div>

                <div className={css.filterBar}>
                    <div className={css.searchWrap}>
                        <i className={`fa-solid fa-magnifying-glass ${css.searchIcon}`} />
                        <input
                            type="text"
                            className={css.searchInput}
                            placeholder="Search comments..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && (
                            <button
                                type="button"
                                className={css.searchClear}
                                onClick={() => setSearch('')}
                                aria-label="Clear search"
                            >
                                <i className="fa-solid fa-xmark" />
                            </button>
                        )}
                    </div>

                    <div className={css.filterGroup} role="tablist" aria-label="Filter by author">
                        {FILTERS.map((f) => {
                            const active = authorFilter === f.key;
                            return (
                                <button
                                    key={f.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    className={`${css.filterPill} ${active ? css.filterPillActive : ''}`}
                                    onClick={() => setAuthorFilter(f.key)}
                                >
                                    <i className={f.icon} />
                                    <span>{f.label}</span>
                                    <span className={css.filterCount}>{counts[f.key]}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className={css.statusGroup} role="tablist" aria-label="Filter by status">
                        {STATUS_FILTERS.map((f) => {
                            const active = statusFilter === f.key;
                            const count = f.key === 'all' ? counts.all : counts[f.key];
                            return (
                                <button
                                    key={f.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    className={`${css.statusPill} ${active ? css.statusPillActive : ''}`}
                                    onClick={() => setStatusFilter(f.key)}
                                >
                                    {f.label}
                                    <span className={css.statusCount}>{count}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="col-md-12">
                    <AnimatePresence mode="popLayout">
                        {visibleComments.length === 0 ? (
                            <motion.div
                                key="empty"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className={css.emptyState}
                            >
                                <i className="fa-regular fa-comment-dots" />
                                <p className="m-0">
                                    {totalLoaded === 0
                                        ? 'No comments yet'
                                        : 'No comments match the current filter'}
                                </p>
                                <p className={css.emptyStateHint}>
                                    {totalLoaded === 0
                                        ? 'Reviewer feedback on this document will appear here.'
                                        : 'Try changing the filter or clearing the search.'}
                                </p>
                                {totalLoaded > 0 && (authorFilter !== 'all' || statusFilter !== 'all' || search) && (
                                    <button
                                        type="button"
                                        className={css.clearFiltersBtn}
                                        onClick={() => {
                                            setAuthorFilter('all');
                                            setStatusFilter('all');
                                            setSearch('');
                                        }}
                                    >
                                        Clear filters
                                    </button>
                                )}
                            </motion.div>
                        ) : (
                            visibleComments.map((comment) =>
                                !comment.resolved ? Unresolved(comment) : Resolved(comment)
                            )
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}
